import { z } from 'zod';
import { cardPaymentSchema } from './billing';
import { US_STATE_CODES } from '../enums';
import type { PlanType } from '../enums';
import {
  BUILT_HANDOVER_KEYS,
  BYOD_BRAND_CODES,
  BYOD_SKU_TYPES,
  ENROLLMENT_SOURCES,
  ENROLLMENT_STATUSES,
  MAILING_TYPES,
  MAX_ADVANCE_MONTHS,
  PORT_CURRENT_CARRIERS,
} from '../enums.customers';
import type {
  CustomerStatus,
  EnrollmentSource,
  EnrollmentStatus,
  HandoverKey,
  MailingType,
  PaymentMethodKey,
} from '../enums.customers';
import { digits, listQuerySchema, objectId, optionalText } from './common';
import { zip5 } from './configuration';
import { simNumber } from './inventory';
import type { ReviewStatus } from './screens';
import type { UserRef } from './users';

/**
 * Prepaid/postpaid enrollment (reference PC1142, PC1170 to PC1172).
 * Steps 1 to 3 follow the reference wizard field by field (docs/product-inventory.md,
 * appendix part 9). Step 4 (payment) was not seen in the reference.
 */

const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);
const PO_BOX = /\bp\.?\s*o\.?\s*box\b/i;

/* ------------------------------ Step 1: ZIP ------------------------------- */

export const enrollmentStartSchema = z.object({ zip: zip5 });
export type EnrollmentStartInput = z.input<typeof enrollmentStartSchema>;

/* ----------------------- Step 2: personal information --------------------- */

const mailingSchema = z.object({
  line1: z.string().trim().min(1, 'Mailing address 1 is required.').max(120),
  line2: z.string().trim().max(120).optional().default(''),
  zip: zip5,
  city: z.string().trim().min(1, 'City is required.').max(60),
  state: z.enum(US_STATE_CODES, { required_error: 'State is required.' }),
});

/**
 * ZIP and state are fixed by step 1 and are not sent again. The messages are worded as the
 * reference words them ("First Name is required.").
 */
export const enrollmentStep2Schema = z
  .object({
    firstName: z.string().trim().min(1, 'First Name is required.').max(30),
    middleInitial: z.string().trim().max(1, 'Middle Initial is one letter.').optional().default(''),
    lastName: z.string().trim().min(1, 'Last Name is required.').max(30),
    addressLine1: z.string().trim().min(1, 'Address 1 is required.').max(120),
    addressLine2: z.string().trim().max(120).optional().default(''),
    city: z.string().trim().min(1, 'City is required.').max(60),
    mailingType: z.enum(MAILING_TYPES).default('same'),
    mailing: mailingSchema.nullable().optional(),
    phone: z.preprocess(blankToUndefined, digits(10, 10, 'Phone Number').optional()),
    /** Whether it is required is a system setting (SC10), checked by the server. */
    email: z.string().trim().max(120).email('Email is not valid.').or(z.literal('')).default(''),
    planId: z.string().regex(/^[a-f\d]{24}$/i, 'Choose plan.'),
  })
  .superRefine((v, ctx) => {
    if (PO_BOX.test(v.addressLine1))
      ctx.addIssue({
        code: 'custom',
        path: ['addressLine1'],
        message: 'PO BOX are not allowed as a service address.',
      });
    if (v.mailingType !== 'same' && !v.mailing)
      ctx.addIssue({
        code: 'custom',
        path: ['mailing', 'line1'],
        message: 'Mailing address 1 is required.',
      });
  });
export type EnrollmentStep2Input = z.input<typeof enrollmentStep2Schema>;

/* ---------------------------- Step 3: handover ---------------------------- */

/**
 * `sim-in-hand` needs the SIM and its carrier. `shipment` needs the model to ship instead:
 * the SIM is assigned later, when the order is fulfilled from the shipping queue. `port-in` is a
 * SIM in hand plus the number to bring and the old account's billing details (the reference's
 * fields and lengths; its required marks); its PIN is asked at payment, where it is sent and never
 * stored. `byod` is a SIM in hand on the customer's own device, with UICCID, Brand Code and SKU Type.
 */
export const enrollmentStep3Schema = z
  .object({
    // Checked below with the other fields, so that one answer names every problem.
    handoverType: z.string().trim().default(''),
    sim: z.preprocess(blankToUndefined, simNumber.optional()),
    /** Only for a SIM that already has a number; otherwise the carrier assigns one on activation. */
    mdn: z.preprocess(blankToUndefined, digits(10, 10, 'MDN').optional()),
    carrierId: z.preprocess(
      blankToUndefined,
      z
        .string()
        .regex(/^[a-f\d]{24}$/i, 'Select Carrier.')
        .optional(),
    ),
    /** Shipment: the model the customer asked for. */
    modelId: z.preprocess(
      blankToUndefined,
      z
        .string()
        .regex(/^[a-f\d]{24}$/i, 'Select Model.')
        .optional(),
    ),
    /** "Advance Billing Month": months paid ahead on top of the first cycle. */
    advanceMonths: z.number().int().min(0).max(MAX_ADVANCE_MONTHS).default(0),
    // Port In: the reference's fields. Optional here and checked below for this handover only.
    portMdn: z.string().trim().max(10).optional(),
    billFirstName: z.string().trim().max(30).optional(),
    billMiddleName: z.string().trim().max(30).optional(),
    billLastName: z.string().trim().max(30).optional(),
    currentCarrier: z.string().trim().max(20).optional(),
    newNumLocalSp: z.string().trim().max(15).optional(),
    streetType: z.string().trim().max(30).optional(),
    streetDirection: z.string().trim().max(30).optional(),
    portZip: z.string().trim().max(5).optional(),
    portCity: z.string().trim().max(60).optional(),
    portState: z.string().trim().max(2).optional(),
    portAddress1: z.string().trim().max(120).optional(),
    portAddress2: z.string().trim().max(120).optional(),
    accountNumber: z.string().trim().max(30).optional(),
    /**
     * "Device ID" (max 25 on the reference's step 3): the customer's handset (IMEI, MEID or ESN),
     * passed to the carrier at activation. Assumption: letters and digits only, not asked for a
     * shipment (the device then comes from inventory).
     */
    deviceId: z.preprocess(
      blankToUndefined,
      z
        .string()
        .trim()
        .max(25, 'Device ID must be at most 25 characters')
        .regex(/^[A-Za-z0-9]+$/, 'Device ID may contain letters and digits only')
        .optional(),
    ),
    // BYOD.
    uiccid: z.string().trim().max(25).optional(),
    brandCode: z.string().trim().max(40).optional(),
    skuType: z.string().trim().max(40).optional(),
  })
  .superRefine((v, ctx) => {
    const need = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (!(BUILT_HANDOVER_KEYS as readonly string[]).includes(v.handoverType))
      need('handoverType', 'Select Handover Type.');
    if (v.handoverType === 'shipment') {
      if (!v.modelId) need('modelId', 'Select Model.');
    } else {
      if (!v.sim) need('sim', 'SIM must be 18 to 22 digits');
      if (!v.carrierId) need('carrierId', 'Select Carrier.');
    }
    if (v.handoverType === 'port-in') {
      if (!/^\d{10}$/.test(v.portMdn ?? '')) need('portMdn', 'Number to port must be 10 digits');
      if (!v.billFirstName) need('billFirstName', 'First Name (Billing) is required');
      if (!v.billLastName) need('billLastName', 'Last Name (Billing) is required');
      if (
        v.currentCarrier &&
        !(PORT_CURRENT_CARRIERS as readonly string[]).includes(v.currentCarrier)
      )
        need('currentCarrier', 'Select Current Wireless Carrier');
      if (!/^\d{5}$/.test(v.portZip ?? '')) need('portZip', 'Zip Code must be 5 digits');
      if (!v.portCity) need('portCity', 'City is required');
      if (!(US_STATE_CODES as readonly string[]).includes((v.portState ?? '').toUpperCase()))
        need('portState', 'State is required');
      if (!v.portAddress1) need('portAddress1', 'Address 1 is required');
      if (!v.accountNumber) need('accountNumber', 'Account Number is required');
    }
    if (v.handoverType === 'byod') {
      if (v.brandCode && !(BYOD_BRAND_CODES as readonly string[]).includes(v.brandCode))
        need('brandCode', 'Select Brand Code');
      if (v.skuType && !(BYOD_SKU_TYPES as readonly string[]).includes(v.skuType))
        need('skuType', 'Select SKU Type');
    }
  })
  .transform((v) => ({
    ...v,
    handoverType: v.handoverType as (typeof BUILT_HANDOVER_KEYS)[number],
  }));
export type EnrollmentStep3Input = z.input<typeof enrollmentStep3Schema>;

/* ---------------------------- Step 4: payment ----------------------------- */

export const enrollmentPaymentSchema = z.object({
  method: z.enum(['cash', 'skip', 'dealerWallet', 'card']),
  card: cardPaymentSchema.optional(),
  /** "Receipt number" (max 40 on the reference's step 4), kept with an Agent cash payment. */
  receiptNumber: z.string().trim().max(40).optional().default(''),
  /** "Port PIN/Password" of a Port In order: sent to the carrier with the request, never stored. */
  portPin: z.string().trim().max(20).optional().default(''),
});
export type EnrollmentPaymentInput = z.input<typeof enrollmentPaymentSchema>;

/* ---------------------------------- Lists --------------------------------- */

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
  .optional();

export const enrollmentListQuerySchema = listQuerySchema.extend({
  status: z.enum(ENROLLMENT_STATUSES).optional(),
  dateFrom: isoDate,
  dateTo: isoDate,
  /** Customer first name, last name, enrollment ID, phone or email. */
  q: optionalText(60),
  source: z.enum(ENROLLMENT_SOURCES).optional(),
  /** Deepest agent chosen in the Master / Distributor / Retailer / Employee filter. */
  agentId: objectId.optional(),
  /** Prepaid, Postpaid, Lifeline or Charity (switch `account-type-filters`). */
  accountType: z
    .enum(['Prepaid', 'Postpaid', 'Lifeline', 'Charity'])
    .optional()
    .or(z.literal('').transform(() => undefined)),
});
export type EnrollmentListQuery = z.input<typeof enrollmentListQuerySchema>;

/* ----------------------------------- DTOs --------------------------------- */

/**
 * "Overall Cost Details" of step 3.
 * totalBeforeTax = plan + advance + activationFee + handset + regulatoryFee + additional - discount.
 * Assumption: advance billing is plan price times the months chosen and nothing when no month is
 * chosen (the reference showed one month's cost with nothing selected; its rule was not clear).
 * Tax is 0 until a tax engine is connected.
 */
export interface EnrollmentCosts {
  planCents: number;
  advanceMonths: number;
  advanceCents: number;
  activationFeeCents: number;
  handsetCents: number;
  regulatoryFeeCents: number;
  additionalCents: number;
  discountCents: number;
  totalBeforeTaxCents: number;
  taxCents: number;
  totalCents: number;
}

/**
 * The tax of an order line by line, as step 4 of the reference lists it (Plan tax, Regulatory
 * fee tax, Activation tax, Handset tax, Plan additional service tax). They add up to `taxCents`.
 */
export interface EnrollmentTaxLines {
  planCents: number;
  regulatoryFeeCents: number;
  activationFeeCents: number;
  handsetCents: number;
  additionalCents: number;
}

export interface EnrollmentApplicant {
  firstName: string;
  middleInitial: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  mailingType: MailingType;
  mailing: { line1: string; line2: string; city: string; state: string; zip: string } | null;
  phone: string;
  email: string;
}

export interface EnrollmentDto {
  id: string;
  enrollmentId: string;
  status: EnrollmentStatus;
  /** The step the user has to do next (2, 3 or 4); null once complete. */
  step: number | null;
  incompleteReason: string | null;
  source: EnrollmentSource;
  zip: string;
  /** City known for the ZIP, to pre-fill step 2; empty when the service area has none. */
  city: string;
  state: string;
  applicant: EnrollmentApplicant | null;
  plan: {
    id: string;
    name: string;
    code: number;
    type: PlanType;
    priceCents: number;
    activationFeeCents: number;
    carrier: { id: string; code: string; name: string };
  } | null;
  /**
   * ZIP carrier routing: the carriers this order may go on, picked one first (SC73 order). Those
   * that cover the ZIP and, once a plan is chosen, on which the plan has a code. Empty while the
   * switch is off: the plan's own carrier is then the only one.
   */
  carrierChoices: Array<{ id: string; code: string; name: string }>;
  handover: {
    type: HandoverKey;
    /** Empty for a shipment order until it is fulfilled. */
    sim: string;
    mdn: string | null;
    carrier: { id: string; code: string; name: string };
    /** Shipment: the model the customer asked for. */
    model: { id: string; name: string } | null;
    /** Port In: the number to bring and the old account (the PIN is never kept). */
    portIn: {
      mdn: string;
      firstName: string;
      middleName: string;
      lastName: string;
      currentCarrier: string;
      newNumLocalSp: string;
      streetType: string;
      streetDirection: string;
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
      accountNumber: string;
    } | null;
    /** BYOD: the customer's own device. */
    byod: { uiccid: string; brandCode: string; skuType: string } | null;
    /** The customer's handset (IMEI, MEID or ESN) when it was given; empty otherwise. */
    deviceId: string;
  } | null;
  /** Set when fraud prevention sent the order to the review queue. */
  review: {
    status: ReviewStatus;
    reason: string;
    decidedBy: UserRef | null;
    decidedAt: string | null;
  } | null;
  costs: (EnrollmentCosts & { taxLines: EnrollmentTaxLines }) | null;
  /** Payment methods this user may use for this enrollment, from settings and permissions. */
  paymentMethods: PaymentMethodKey[];
  /** The caller's own Dealer Wallet balance, when Dealer wallet is one of `paymentMethods`. */
  dealerWalletCents: number | null;
  payment: {
    method: PaymentMethodKey;
    amountCents: number;
    receiptNumber: string;
    status: 'Paid' | 'Skipped';
    at: string;
  } | null;
  customer: { id: string; customerId: number; mdn: string | null; status: CustomerStatus } | null;
  chain: { master?: UserRef; distributor?: UserRef; retailer?: UserRef; employee?: UserRef };
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

/** Plan offered at step 2: live in the enrollment's state and sold on the caller's channel. */
export interface EnrollmentPlanOption {
  id: string;
  name: string;
  type: PlanType;
  priceCents: number;
  activationFeeCents: number;
}

/** Row of the All / Completed / Incomplete enrollment lists. */
export interface EnrollmentListItemDto {
  id: string;
  enrollmentId: string;
  name: string;
  contactNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  planName: string;
  planPriceCents: number | null;
  /** "Phone cost": the handset of a shipment order (0 for a SIM in hand); null before step 3. */
  phoneCostCents: number | null;
  amountPaidCents: number | null;
  paymentMode: string;
  enrollmentDate: string;
  sim: string;
  mdn: string;
  step: number | null;
  status: EnrollmentStatus;
  incompleteReason: string;
  /** "SIM/Device assignment status" on the completed list. */
  assignmentStatus: string;
  source: EnrollmentSource;
}

/** One line of the "Enrollment lifecycle" dialog. */
export interface EnrollmentEventDto {
  label: string;
  at: string;
  by: UserRef | null;
}

/** One line of the per-enrollment API log: #, Api, Request, Response, Date time. */
export interface ApiLogDto {
  id: string;
  api: string;
  action: string;
  request: Record<string, unknown>;
  response: Record<string, unknown>;
  status: 'success' | 'failure';
  at: string;
}
